import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '../api/client';
import { createKlient, listKlienti, type KlientListItem, type KlientStatus } from '../api/klienti';
import { useFeather } from '../shell/useFeather';
import { KLIENT_STATUSES, KLIENT_STATUS_DEF, klientiConfigFor } from './klienti-config';
import { fmtDay, fmtEur, klientInitials } from './klienti-format';
import { KlientDetailView } from './KlientDetailView';
import { MarketPill, StatusPill, WebLink } from './klienti-pills';
import '../affilmaster/affilmaster-detail.css';
import { logoFrameStyle } from './logo-compose';
import './klienti.css';
import { Button } from '../components/arc/button/button';
import { Dialog, DialogContent } from '../components/arc/dialog/dialog';
import { PageState } from '../components/arc/page-state/page-state';
import { ArcPage, ArcPageHeader, ArcSummaryGrid, ArcSummaryMetric, ArcTableFrame } from '../components/arc/primitives/arc-primitives';
import { Select } from '../components/arc/select/select';
import { SortableDataTable, type DataColumn } from '../components/arc/sortable-data-table/sortable-data-table';
import { ServiceBar } from '../shell/ServiceBar';

const LOGO_STYLE = { width: 34, height: 34, objectFit: 'contain', borderRadius: 10, background: '#fff', padding: 3 } as const;

function KlientLogoMini({ logo, logoBg, name }: { logo: string | null; logoBg: string | null; name: string }) {
  const [err, setErr] = useState(false);
  if (logo && !err) return <img src={logo} alt="" className="klient-logo-mini" style={{ ...LOGO_STYLE, ...logoFrameStyle(logoBg) }} onError={() => setErr(true)} />;
  return <span className="prototype-company-avatar">{klientInitials(name)}</span>;
}

const LIMITS = ['25', '50', '100', '200', 'all'];
type ClientTableRow = KlientListItem & Record<string, unknown>;

export interface KlientiPageProps {
  space: string;
  detailId?: string;
  onOpenDetail: (id: string) => void;
  onCloseDetail: () => void;
  onOpenLead?: (leadId: number) => void;
}

export function KlientiPage({ space, detailId, onOpenDetail, onCloseDetail, onOpenLead }: KlientiPageProps) {
  const cfg = klientiConfigFor(space);
  const [items, setItems] = useState<KlientListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [limit, setLimit] = useState('all');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [product, setProduct] = useState('all');
  const [showAdd, setShowAdd] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    listKlienti()
      .then((data) => { if (!cancelled) { setItems(data); setError(null); } })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof ApiError ? e.message : 'Načtení klientů selhalo'); });
    return () => { cancelled = true; };
  }, [reloadKey]);

  const marketLabel = (code: string | null) => cfg.markets.find((m) => m.code === code)?.label ?? code;
  const products = useMemo(() => {
    const set = new Set<string>(cfg.products);
    for (const k of items ?? []) if (k.product) set.add(k.product);
    return [...set];
  }, [items, cfg.products]);

  const filtered = useMemo(() => {
    if (!items) return [];
    const q = query.trim().toLowerCase();
    return items.filter((k) => {
      if (status !== 'all' && k.status !== status) return false;
      if (product !== 'all' && (k.product ?? '') !== product) return false;
      if (q && !`${k.name} ${k.company ?? ''} ${k.website ?? ''} ${k.product ?? ''}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, query, status, product]);
  const visible = useMemo(() => (limit === 'all' ? filtered : filtered.slice(0, Number(limit))), [filtered, limit]);

  const total = items?.length ?? 0;
  const activeTotal = useMemo(() => (items ?? []).filter((k) => k.status === 'active').length, [items]);
  const monthlyTotal = useMemo(() => (items ?? []).filter((k) => k.status === 'active').reduce((s, k) => s + (k.monthlyFeeEur ?? 0), 0), [items]);
  const tableRows: ClientTableRow[] = visible.map((item) => ({ ...item }));
  const columns: DataColumn<ClientTableRow>[] = [
    {
      key: 'name', label: 'Klient', sortable: true,
      render: (_value, row) => (
        <a
          className="klienti-table-client-link"
          href={`${window.location.pathname.replace(/\/$/, '')}/${row.id}`}
          onClick={(event) => { event.preventDefault(); onOpenDetail(String(row.id)); }}
        >
          <KlientLogoMini logo={row.logo} logoBg={row.logoBg} name={row.name} />
          <span><strong>{row.name}</strong>{row.company && row.company !== row.name ? <small>{row.company}</small> : null}</span>
        </a>
      ),
    },
    { key: 'website', label: 'Web', render: (_value, row) => <WebLink url={row.website} /> },
    { key: 'market', label: 'Země', sortable: true, render: (_value, row) => <MarketPill market={row.market} label={marketLabel(row.market)} /> },
    ...(!cfg.billing ? [
      { key: 'product', label: 'Produkt', sortable: true, render: (_value: unknown, row: ClientTableRow) => row.product ?? '—' },
      { key: 'module', label: 'Modul', sortable: true, render: (_value: unknown, row: ClientTableRow) => row.module ?? '—' },
      { key: 'monthlyFeeEur', label: 'Měsíčně', sortable: true, numeric: true, render: (_value: unknown, row: ClientTableRow) => row.monthlyFeeEur != null ? fmtEur(row.monthlyFeeEur) : '—' },
    ] satisfies DataColumn<ClientTableRow>[] : []),
    { key: 'clientSince', label: 'Klient od', sortable: true, render: (_value, row) => fmtDay(row.clientSince) },
    { key: 'status', label: 'Stav', sortable: true, render: (_value, row) => <StatusPill status={row.status} /> },
  ];

  useFeather([items, detailId, limit, query, status, product, showAdd]);

  const selectedId = detailId ? Number(detailId) : null;
  if (selectedId != null && Number.isFinite(selectedId)) {
    return (
      <KlientDetailView
        cfg={cfg}
        id={selectedId}
        onBack={onCloseDetail}
        onDeleted={() => { onCloseDetail(); setReloadKey((k) => k + 1); }}
        onChanged={() => setReloadKey((k) => k + 1)}
        onOpenLead={onOpenLead}
      />
    );
  }

  if (error) {
    return (
      <ArcPage className="klienti-list">
        <ArcPageHeader eyebrow={cfg.eyebrow} title={cfg.title} description={cfg.description} />
        <PageState title="Klienty se nepodařilo načíst" message={error} />
        <Button type="button" variant="secondary" size="sm" onClick={() => setReloadKey((k) => k + 1)}>Načíst znovu</Button>
      </ArcPage>
    );
  }
  if (!items) return <ArcPage className="klienti-list"><PageState title="Načítám klienty" loading /></ArcPage>;

  return (
    <ArcPage className="klienti-list">
      <ArcPageHeader eyebrow={cfg.eyebrow} title={cfg.title} description={cfg.description} meta={<Button type="button" size="sm" onClick={() => setShowAdd(true)}>Přidat klienta</Button>} />

      {showAdd && (
        <AddKlientModal
          withTarif={!cfg.billing}
          products={products}
          markets={cfg.markets}
          onCancel={() => setShowAdd(false)}
          onSaved={(id) => { setShowAdd(false); setReloadKey((k) => k + 1); onOpenDetail(String(id)); }}
        />
      )}

      <ArcSummaryGrid>
        <ArcSummaryMetric label="Klientů celkem" value={total} context="Celá databáze" />
        <ArcSummaryMetric label="Aktivní" value={activeTotal} context={total > 0 ? `${Math.round((activeTotal / total) * 100)} % z celku` : 'Žádný aktivní klient'} />
        {!cfg.billing ? <ArcSummaryMetric label="Měsíčně celkem" value={monthlyTotal} suffix="€" context="Aktivní klienti" /> : null}
        <ArcSummaryMetric label="Zobrazeno" value={visible.length} context={`Z ${total} klientů`} />
      </ArcSummaryGrid>

      <ServiceBar
        variant="seg"
        selects={[
          { label: 'Stav', value: status, onChange: setStatus, options: [{ value: 'all', label: 'Všechny' }, ...KLIENT_STATUSES.map((item) => ({ value: item, label: KLIENT_STATUS_DEF[item].label }))] },
          ...(!cfg.billing ? [{ label: 'Produkt', value: product, onChange: setProduct, options: [{ value: 'all', label: 'Všechny' }, ...products.map((item) => ({ value: item, label: item }))] }] : []),
          { label: 'Limit', value: limit, onChange: setLimit, options: LIMITS.map((item) => ({ value: item, label: item === 'all' ? 'Vše' : item })) },
        ]}
        onReset={() => { setStatus('all'); setProduct('all'); setLimit('all'); setQuery(''); }}
        search={query}
        onSearch={setQuery}
        searchPlaceholder="Hledat klienta, společnost, web nebo tarif…"
        right={<span className="klienti-list-count">{visible.length} z {total} klientů</span>}
      />

      <ArcTableFrame label="Seznam klientů">
        <SortableDataTable rows={tableRows} columns={columns} rowKey="id" caption="Seznam klientů" emptyMessage="Žádní klienti neodpovídají filtru." />
      </ArcTableFrame>
    </ArcPage>
  );
}

function AddKlientModal({ withTarif, products, markets, onCancel, onSaved }: {
  withTarif: boolean;
  products: string[];
  markets: Array<{ code: string; label: string }>;
  onCancel: () => void;
  onSaved: (id: number) => void;
}) {
  const [form, setForm] = useState({ name: '', company: '', ic: '', vat: '', website: '', market: markets[0]?.code ?? 'CZ', product: '', monthlyFeeEur: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    if (form.name.trim().length < 2) { setError('Název klienta musí mít alespoň 2 znaky'); return; }
    const fee = form.monthlyFeeEur.trim() === '' ? null : Number(form.monthlyFeeEur.replace(',', '.'));
    if (fee != null && (!Number.isFinite(fee) || fee < 0)) { setError('Měsíční částka není platné číslo'); return; }
    setSaving(true); setError(null);
    try {
      const res = await createKlient({
        name: form.name.trim(), company: form.company.trim() || null, ic: form.ic.trim() || null, vat: form.vat.trim() || null,
        website: form.website.trim() || null, market: form.market, product: form.product || null, monthlyFeeEur: fee,
      });
      onSaved(res.id);
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Založení klienta selhalo'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !saving) onCancel(); }}>
      <DialogContent title="Nový klient" description={withTarif ? 'Základní firemní údaje a tarif' : 'Základní firemní údaje'} className="klienti-create-dialog p1-arc-scope">
        <form className="klienti-create-form" onSubmit={(event) => { event.preventDefault(); void save(); }}>
          {error && <p className="klienti-create-error" role="alert">{error}</p>}
          <div className="klienti-create-field klienti-create-full"><label htmlFor="klient-create-name">Název klienta *</label><input id="klient-create-name" autoFocus required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div className="klienti-create-field klienti-create-full"><label htmlFor="klient-create-company">Společnost</label><input id="klient-create-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Právní název firmy" /></div>
          <div className="klienti-create-field"><label htmlFor="klient-create-ic">IČ</label><input id="klient-create-ic" value={form.ic} onChange={(e) => setForm({ ...form, ic: e.target.value })} /></div>
          <div className="klienti-create-field"><label htmlFor="klient-create-vat">DIČ</label><input id="klient-create-vat" value={form.vat} onChange={(e) => setForm({ ...form, vat: e.target.value })} /></div>
          <div className="klienti-create-field klienti-create-full"><label htmlFor="klient-create-web">Web</label><input id="klient-create-web" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="www.example.com" /></div>
          <Select label="Země" value={form.market} onValueChange={(market) => setForm({ ...form, market })} options={markets.map((market) => ({ value: market.code, label: market.label }))} />
          {withTarif && <Select label="Produkt" value={form.product || 'none'} onValueChange={(product) => setForm({ ...form, product: product === 'none' ? '' : product })} options={[{ value: 'none', label: 'Nevybráno' }, ...products.map((item) => ({ value: item, label: item }))]} />}
          {withTarif && <div className="klienti-create-field"><label htmlFor="klient-create-fee">Měsíčně (EUR)</label><input id="klient-create-fee" inputMode="decimal" value={form.monthlyFeeEur} onChange={(e) => setForm({ ...form, monthlyFeeEur: e.target.value })} placeholder="0" /></div>}
          <div className="klienti-create-actions"><Button type="button" variant="secondary" size="sm" disabled={saving} onClick={onCancel}>Zrušit</Button><Button type="submit" size="sm" loading={saving}>Vytvořit klienta</Button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
